/**
 * API常量定义
 * 统一管理API相关的常量值，状态码、枚举选项等
 */

// ===== API类型选项 =====

/**
 * API类型选项（用于UI组件）
 */
export const API_TYPE_OPTIONS = [
  { label: '翻译API', value: 'translation' },
  { label: '语音API', value: 'speech' },
  { label: 'AI模型API', value: 'ai_model' }
] as const

/**
 * 用量类型选项（用于UI组件）
 */
export const USAGE_TYPE_OPTIONS = [
  { label: '字符型', value: 'character' },
  { label: '时间型', value: 'time' }
] as const

// ===== 项目状态选项 =====

/**
 * 项目状态选项（用于UI组件）
 */
export const PROJECT_STATUS_OPTIONS = [
  { label: '草稿', value: 'draft' },
  { label: '待处理', value: 'pending' },
  { label: '处理中', value: 'processing' },
  { label: '已完成', value: 'completed' },
  { label: '失败', value: 'failed' },
  { label: '已过期', value: 'expired' },
  { label: '已清理', value: 'cleaned' }
] as const

/**
 * 项目类型选项（用于UI组件）
 */
export const PROJECT_TYPE_OPTIONS = [
  { label: '视频项目', value: 'video' },
  { label: '文档项目', value: 'document' }
] as const

// ===== 任务状态选项 =====

/**
 * 任务状态选项（用于UI组件）
 */
export const TASK_STATUS_OPTIONS = [
  { label: '待处理', value: 'pending' },
  { label: '处理中', value: 'processing' },
  { label: '已完成', value: 'completed' },
  { label: '已取消', value: 'cancelled' },
  { label: '已暂停', value: 'paused' },
  { label: '审核中', value: 'reviewing' },
  { label: '已拒绝', value: 'rejected' }
] as const

/**
 * 任务类型选项（用于UI组件）
 */
export const TASK_TYPE_OPTIONS = [
  { label: '个人任务', value: 'self' },
  { label: '组内任务', value: 'group' },
  { label: '跨语种任务', value: 'metalingual' }
] as const

// ===== 用户相关选项 =====

/**
 * 用户类型选项（用于UI组件）
 */
export const USER_TYPE_OPTIONS = [
  { label: '普通用户', value: 'normal' },
  { label: '受限用户', value: 'restricted' },
  { label: '管理员', value: 'admin' }
] as const

/**
 * 用户状态选项（用于UI组件）
 */
export const USER_STATUS_OPTIONS = [
  { label: '活跃', value: 'active' },
  { label: '非活跃', value: 'inactive' },
  { label: '已暂停', value: 'suspended' }
] as const

// ===== 分页常量 =====

/**
 * 默认分页大小
 */
export const DEFAULT_PAGE_SIZE = 20

/**
 * 分页大小选项
 */
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const

/**
 * 最大分页大小
 */
export const MAX_PAGE_SIZE = 100

// ===== HTTP状态码常量 =====

/**
 * 常用HTTP状态码
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503
} as const

// ===== 文件相关常量 =====

/**
 * 支持的文件类型
 */
export const SUPPORTED_FILE_TYPES = {
  VIDEO: ['.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv'],
  DOCUMENT: ['.docx', '.doc', '.pdf', '.txt', '.rtf'],
  IMAGE: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'],
  AUDIO: ['.mp3', '.wav', '.aac', '.m4a', '.ogg']
} as const

/**
 * 文件大小限制（字节）
 */
export const FILE_SIZE_LIMITS = {
  VIDEO: 2 * 1024 * 1024 * 1024, // 2GB
  DOCUMENT: 100 * 1024 * 1024,   // 100MB
  IMAGE: 10 * 1024 * 1024,       // 10MB
  AUDIO: 500 * 1024 * 1024       // 500MB
} as const

// ===== 语言相关常量 =====

/**
 * 常用语言代码映射
 */
export const LANGUAGE_CODES = {
  'zh-CN': '简体中文',
  'zh-TW': '繁体中文',
  'en': '英语',
  'en-US': '美式英语',
  'en-GB': '英式英语',
  'ja': '日语',
  'ko': '韩语',
  'fr': '法语',
  'de': '德语',
  'es': '西班牙语',
  'it': '意大利语',
  'pt': '葡萄牙语',
  'ru': '俄语',
  'ar': '阿拉伯语',
  'th': '泰语',
  'vi': '越南语'
} as const

// ===== 时间相关常量 =====

/**
 * 时间格式常量
 */
export const DATE_FORMATS = {
  DATE: 'YYYY-MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  DATETIME_MINUTE: 'YYYY-MM-DD HH:mm',
  ISO: 'YYYY-MM-DDTHH:mm:ss.SSSZ'
} as const

/**
 * 时间范围选项（用于筛选）
 */
export const TIME_RANGE_OPTIONS = [
  { label: '今天', value: 'today' },
  { label: '昨天', value: 'yesterday' },
  { label: '最近7天', value: 'last_7_days' },
  { label: '最近30天', value: 'last_30_days' },
  { label: '最近90天', value: 'last_90_days' },
  { label: '自定义', value: 'custom' }
] as const

// ===== 优先级常量 =====

/**
 * 任务优先级选项
 */
export const PRIORITY_OPTIONS = [
  { label: '低', value: 'low', color: '#52c41a' },
  { label: '中', value: 'medium', color: '#faad14' },
  { label: '高', value: 'high', color: '#f5222d' },
  { label: '紧急', value: 'urgent', color: '#a0275f' }
] as const

// ===== 导出配置常量 =====

/**
 * 导出格式选项
 */
export const EXPORT_FORMATS = [
  { label: 'SRT字幕', value: 'srt' },
  { label: 'ASS字幕', value: 'ass' },
  { label: 'VTT字幕', value: 'vtt' },
  { label: 'Word文档', value: 'docx' },
  { label: 'PDF文档', value: 'pdf' },
  { label: 'Excel表格', value: 'xlsx' }
] as const

// ===== 系统配置常量 =====

/**
 * 系统模块列表
 */
export const SYSTEM_MODULES = [
  'user_management',
  'project_management', 
  'task_management',
  'document_management',
  'video_management',
  'translation_service',
  'ai_service',
  'system_config'
] as const

/**
 * 权限操作类型
 */
export const PERMISSION_ACTIONS = [
  'create',
  'read', 
  'update',
  'delete',
  'assign',
  'approve',
  'export'
] as const